import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <div className="mb-8">
          <Link to="/" className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1">
            ← Back to Home
          </Link>
        </div>

        <header className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Terms of Service</h1>
          <p className="text-white/70">RestroAI – AI-Powered Restaurant Management Platform</p>
          <p className="text-white/50 text-sm mt-2">Last updated: February 2026</p>
        </header>

        <div className="space-y-8 text-white/90 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using the RestroAI mobile application, website, and related services (&quot;Services&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, do not use our Services. We may update these Terms from time to time; continued use of the Services after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Description of Services</h2>
            <p>
              RestroAI provides an AI-powered restaurant management platform, including but not limited to: order management, menu and table management, analytics and insights, staff management, push notifications, QR code menu features, and customer-facing ordering. We reserve the right to modify, suspend, or discontinue any part of the Services with reasonable notice where practicable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Account and Registration</h2>
            <p>
              You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You must notify us promptly of any unauthorized use. We are not liable for any loss or damage arising from your failure to protect your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Acceptable Use</h2>
            <p className="mb-3">You agree to use the Services only for lawful purposes and in accordance with these Terms. You must not:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Services in any way that violates applicable laws or regulations.</li>
              <li>Infringe the intellectual property or other rights of RestroAI or any third party.</li>
              <li>Transmit malware, spam, or any harmful or illegal content.</li>
              <li>Attempt to gain unauthorized access to our systems, other accounts, or third-party data.</li>
              <li>Use the Services to harass, abuse, or harm others.</li>
              <li>Resell, sublicense, or commercially exploit the Services in a manner not permitted by your agreement with us.</li>
            </ul>
            <p className="mt-3">We may suspend or terminate your access if we reasonably believe you have violated these Terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Your Data and Privacy</h2>
            <p>
              Your use of the Services is also governed by our <Link to="/privacy-policy" className="text-blue-300 hover:text-blue-200 underline">Privacy Policy</Link>, which describes how we collect, use, and protect your information. By using the Services, you consent to such processing. You are responsible for ensuring that any data you upload or process (including customer and staff data) is collected and used in compliance with applicable privacy laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Intellectual Property</h2>
            <p>
              RestroAI and its licensors own all rights, title, and interest in the Services, including software, design, text, graphics, and other content (excluding content you provide). You may not copy, modify, distribute, or create derivative works from our Services without our prior written consent. You retain ownership of content you submit; you grant us a license to use, store, and process that content as necessary to provide and improve the Services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Payment and Subscriptions</h2>
            <p>
              If you subscribe to paid features, you agree to pay all applicable fees as described at the time of purchase or in your plan. Fees may be charged on a recurring basis (e.g. monthly or annually). Refund policies are as stated at the time of purchase or in separate agreement. We may change fees with reasonable notice; continued use after a fee change may constitute acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Disclaimers</h2>
            <p>
              The Services are provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind, express or implied. We do not warrant that the Services will be uninterrupted, error-free, or free of harmful components. We are not responsible for the accuracy of data you input or for business decisions you make based on the Services. To the extent permitted by law, we disclaim all implied warranties, including merchantability and fitness for a particular purpose.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, RestroAI and its affiliates, officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or business opportunities, arising from or related to your use of the Services. Our total liability for any claims arising from these Terms or the Services shall not exceed the amount you paid us in the twelve (12) months preceding the claim, or one hundred (100) currency units, whichever is greater. Some jurisdictions do not allow certain limitations; in such cases, our liability is limited to the fullest extent permitted by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless RestroAI and its affiliates and their respective officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, and expenses (including reasonable legal fees) arising from your use of the Services, your violation of these Terms, or your violation of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Termination</h2>
            <p>
              We may suspend or terminate your access to the Services at any time for cause, including breach of these Terms. You may stop using the Services at any time. Upon termination, your right to use the Services ceases. Provisions that by their nature should survive (including disclaimers, limitation of liability, indemnification, and governing law) will survive termination.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Governing Law and Disputes</h2>
            <p>
              These Terms shall be governed by the laws of India, without regard to conflict of law principles. Any dispute arising from these Terms or the Services shall be subject to the exclusive jurisdiction of the courts of India. You may also have rights under consumer protection laws in your jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Contact</h2>
            <p>
              For questions about these Terms of Service, please contact us:
            </p>
            <ul className="list-none mt-2 space-y-1">
              <li><strong className="text-white">Website:</strong> <a href="https://www.restroai.in" className="text-blue-300 hover:text-blue-200 underline" target="_blank" rel="noopener noreferrer">https://restroai.in</a></li>
              <li><strong className="text-white">Contact page:</strong> <Link to="/contact-us" className="text-blue-300 hover:text-blue-200 underline">RestroAI Contact Us</Link></li>
            </ul>
          </section>
        </div>

        <footer className="mt-12 pt-8 border-t border-white/20 text-white/60 text-sm text-center">
          <p>© {new Date().getFullYear()} RestroAI. All rights reserved.</p>
          <p className="mt-2">
            <Link to="/" className="text-blue-300 hover:text-blue-200">Home</Link>
            {' · '}
            <Link to="/privacy-policy" className="text-blue-300 hover:text-blue-200">Privacy Policy</Link>
            {' · '}
            <Link to="/contact-us" className="text-blue-300 hover:text-blue-200">Contact</Link>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default TermsOfService;
